/**
 * Short confirmation buzz on a good scan and a longer double-buzz on a bad one, so an operator
 * wearing gloves and looking at a rack — not the screen — knows whether to move on. Silently
 * absent on desktop and iOS Safari, which is fine: it only ever supplements the on-screen state.
 *
 * It matters more for a handheld than for the camera: a trigger pull gives no visual cue that
 * anything was read, so without this a scan that the app declined feels identical to one it took.
 */
export const buzz = (pattern: number | number[]) => {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* vibration is a nicety; never let it break a scan */
  }
};

export const BUZZ_ACCEPTED = 40;
export const BUZZ_REJECTED = [60, 80, 60];

/**
 * App-owned tones for what the warehouse did with a scan.
 *
 * The handheld and Scandit each beep when they decode a barcode, which is earlier than — and
 * different from — the app knowing the scan was a hit, a match, or a pick. These play at that
 * later moment. A success beep rises; a fail warble drops. Both are synthesized, so they work
 * offline and need no sound files.
 */
export type ToneStep = {
  frequency: number;
  /** Seconds after the tone starts. */
  offset: number;
  duration: number;
  type: OscillatorType;
  /**
   * Peak level, 0–1. Above 1 the output clips into a crackle on a phone speaker rather than
   * getting louder.
   */
  gain: number;
  /**
   * Seconds to sit at peak before fading. Without it a note starts fading the moment it peaks,
   * which on a busy floor reads as a tick rather than a beep.
   */
  hold?: number;
};

/**
 * One level for both the chime and the fail tone. Deliberately above 1: the output clips, which
 * squares off the sine chime into a harsher, slightly louder sound. The fail tone is already a
 * square wave, so clipping leaves it as it sounds at 1.
 */
export const SCAN_TONE_GAIN = 2;

/**
 * Both tones sit where they carry over pick-floor noise. Hearing is most sensitive around
 * 2–4 kHz and phone and handheld speakers are loudest there, while cart and foot-traffic rumble
 * sits low and swallows anything near it.
 */
const scanBeep = (frequency: number, offset: number): ToneStep => ({
  frequency,
  offset,
  duration: 0.15,
  type: 'sine',
  gain: SCAN_TONE_GAIN,
  hold: 0.1
});

/** Two quick, high beeps, the second a step higher. */
export const SUCCESS_TONE: ToneStep[] = [scanBeep(2400, 0), scanBeep(2800, 0.2)];

const warbleNote = (frequency: number, offset: number): ToneStep => ({
  frequency,
  offset,
  duration: 0.1,
  type: 'square',
  gain: SCAN_TONE_GAIN,
  hold: 0.07
});

/**
 * A warble — high, low, three times — like an alarm, which is what makes it hard to miss. It stays
 * well below the success beeps so the two cannot be confused, but above the floor's rumble.
 */
export const FAIL_TONE: ToneStep[] = [0, 1, 2].flatMap((cycle) => [
  warbleNote(1500, cycle * 0.22),
  warbleNote(1000, cycle * 0.22 + 0.11)
]);

/** A single mid note: the scan was read, and it did not change the list (already checked). */
export const INFO_TONE: ToneStep[] = [
  { frequency: 523, offset: 0, duration: 0.12, type: 'sine', gain: 0.35 }
];

const PENDING_HIT_KEY = 'rxerp.scan.pendingHit';
/** Long enough for the item lookup to finish, short enough that a later visit to the same screen stays quiet. */
const PENDING_HIT_MS = 20000;

let audioCtx: AudioContext | null = null;

const audioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) audioCtx = new Ctor();
  return audioCtx;
};

/** A scan is often the first thing that happens after a tap. Resume then, so the tone is allowed to play. */
const armAudio = () => {
  const ctx = audioContext();
  if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {});
};

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', armAudio);
  window.addEventListener('keydown', armAudio);
}

export const scheduleTone = (ctx: AudioContext, steps: ToneStep[]) => {
  steps.forEach((step) => {
    const start = ctx.currentTime + step.offset;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = step.type;
    osc.frequency.value = step.frequency;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(step.gain, start + 0.012);
    if (step.hold) gain.gain.setValueAtTime(step.gain, start + 0.012 + step.hold);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + step.duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(start);
    osc.stop(start + step.duration + 0.02);
  });
};

const playRecipe = async (steps: ToneStep[]) => {
  try {
    const ctx = audioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    if (ctx.state !== 'running') return;
    scheduleTone(ctx, steps);
  } catch {
    /* a missing speaker must never break a scan */
  }
};

export const playSuccessTone = () => playRecipe(SUCCESS_TONE);

export const playFailTone = () => playRecipe(FAIL_TONE);

export const playInfoTone = () => playRecipe(INFO_TONE);

/**
 * Home scan navigates to the item lookup before the hit is known. Mark that navigation so the
 * destination can play success once the item loads, or fail if the lookup errors.
 */
export const expectScanHit = () => {
  try {
    window.sessionStorage.setItem(PENDING_HIT_KEY, String(Date.now()));
  } catch {
    /* private mode: the screen still navigates, it just stays quiet */
  }
};

/** True once, for a navigation `expectScanHit` marked in the last few seconds. */
export const takeExpectedScanHit = (): boolean => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_HIT_KEY);
    if (raw == null) return false;
    window.sessionStorage.removeItem(PENDING_HIT_KEY);
    const at = Number(raw);
    return Number.isFinite(at) && Date.now() - at < PENDING_HIT_MS;
  } catch {
    return false;
  }
};
